import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ReferencedResource } from "../ResourceDetailsContentBlock/ResourceDetailsContentBlock";
import { getResourceIcon } from "@/modules/hub/features/ResourceLibraryV2/utils/getResourceIcon";

interface ResourceReferenceBlockProps {
  references: ReferencedResource[];
  blockTitle?: string | null;
  blockDescription?: string | null;
}

export function ResourceReferenceBlock({
  references,
  blockTitle,
  blockDescription,
}: ResourceReferenceBlockProps) {
  if (!references || references.length === 0) {
    return null;
  }

  // Determine if this is a single reference or multiple
  const isSingleReference = references.length === 1;

  return (
    <div className="my-6">
      {/* Optional block title */}
      {blockTitle && (
        <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
          {blockTitle}
        </h3>
      )}

      {/* Optional block description */}
      {blockDescription && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {blockDescription}
        </p>
      )}

      {/* Display references as cards */}
      <div
        className={`grid gap-4 ${isSingleReference ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}
      >
        {references.map((resource) => (
          <ResourceReferenceCard key={resource.id} resource={resource} />
        ))}
      </div>
    </div>
  );
}

function ResourceReferenceCard({ resource }: { resource: ReferencedResource }) {
  const truncatedDescription = resource.description
    ? resource.description.length > 120
      ? resource.description.substring(0, 120) + "..."
      : resource.description
    : "";

  // Internal resource card
  const resourceType = (resource.resourceType || "resource").toLowerCase();

  return (
    <Link
      className="block group rounded-lg transition-all bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600"
      to={`/hub/resources-v2/${resource.slug}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video">
        {resource.thumbnailUrl ? (
          <img
            alt={resource.title}
            className="object-cover rounded-t-lg w-full h-full"
            src={resource.thumbnailUrl}
          />
        ) : (
          <div className="w-full h-full rounded-t-lg flex items-center justify-center bg-gray-100 dark:bg-slate-700">
            {getResourceIcon(resource.resourceType)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Resource type badge */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300">
            {resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}
          </span>
        </div>

        {/* Title */}
        <h4 className="font-semibold text-base text-gray-900 dark:text-slate-100 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400">
          {resource.title}
        </h4>

        {/* Description */}
        {truncatedDescription && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
            {truncatedDescription}
          </p>
        )}
      </div>
    </Link>
  );
}
